import { Button, Text, useMantineTheme } from "@mantine/core";
import { withPolymorphicClickAnalytics } from "@/analytics";
import { IconButton } from "@/common-ui";
import { Pause } from "@/common-ui/assets/image/Pause";
import type { ConferenceParticipant, VoiceTask } from "@/sdk/agent";
import { useAsyncLoading, useStopwatch } from "@/shared-utils";
import classes from "./Button.module.css";

function ResumeCallButton({
    onHoldSince,
    outlined = true,
    onClick,
    isDisabled,
}: {
    onHoldSince: number | null;
    outlined?: boolean;
    onClick: () => Promise<void>;
    isDisabled?: boolean;
}) {
    const formattedHoldTime = useStopwatch(onHoldSince, false, "m:ss");
    const theme = useMantineTheme();

    const [loading, handleHold] = useAsyncLoading(async () => {
        await onClick();
    });

    const ResumeButton = withPolymorphicClickAnalytics(Button<"button">, "Click");

    return (
        <ResumeButton
            analytics={{
                actionId: "ResumeCallButton",
            }}
            aria-label="Resume Call"
            data-testid="call-controls-resume"
            h={30}
            color={theme.colors.gray[0]}
            loading={loading}
            onClick={() => void handleHold()}
            size="sm"
            variant={outlined ? "outline" : "filled"}
            disabled={isDisabled}
        >
            <Text size="xxs" c={outlined ? theme.white : theme.black}>
                Resume
            </Text>
            <Text ff="mono" c={outlined ? theme.white : theme.black} size="xxs" className={classes.monospaceTimerText}>
                ({formattedHoldTime})
            </Text>
        </ResumeButton>
    );
}

function PauseCallButton({
    outlined,
    onClick,
    isDisabled,
}: {
    outlined?: boolean;
    onClick: () => Promise<void>;
    isDisabled?: boolean;
}) {
    const [loading, handleHold] = useAsyncLoading(async () => {
        await onClick();
    });

    const buttonClass = outlined ? classes.pauseIconBorderBtn : "";

    return (
        <IconButton
            analytics={{
                actionId: "PauseCallButton",
            }}
            data-testid="call-controls-pause"
            loading={loading}
            onClick={() => void handleHold()}
            radius="xl"
            size="30"
            tooltip="Hold"
            classNames={{ root: buttonClass }}
            disabled={isDisabled}
        >
            <Pause height={14} />
        </IconButton>
    );
}

interface HoldButtonProps {
    task: VoiceTask;
    outlined?: boolean;
    isOnHold: boolean;
    onResumeCallButtonClick: () => Promise<void>;
    onPauseCallButtonClick: () => Promise<void>;
    participant?: ConferenceParticipant;
    isDisabled?: boolean;
}

export function HoldButton({
    task,
    outlined = false,
    isOnHold,
    onResumeCallButtonClick,
    onPauseCallButtonClick,
    participant,
    isDisabled,
}: HoldButtonProps) {
    function getHoldTimestamp(): number | null {
        if (!participant) {
            return task.holdState.onHoldSince;
        }
        return participant.hold?.since ?? null;
    }

    const onHoldSince = getHoldTimestamp();
    if (isOnHold)
        return (
            <ResumeCallButton
                outlined
                onClick={onResumeCallButtonClick}
                onHoldSince={onHoldSince}
                isDisabled={isDisabled}
            />
        );
    return <PauseCallButton outlined={outlined} onClick={onPauseCallButtonClick} isDisabled={isDisabled} />;
}
